import React, { useState } from 'react';
import loadable from '@loadable/component';

import { array, bool, object, func } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { exposeLinkProps } from '../PageBuilder/Field/Field.helpers';

import FallbackPage from './FallbackPage';
import { LandingPageAuthAwareLink, LandingPageRoleNoticeModal } from './LandingPageCta';
import { ASSET_NAME } from './LandingPage.duck';
import SectionHowItWorks from '../PageBuilder/SectionBuilder/SectionHowItWorks';
import SectionWhoIsItFor from '../PageBuilder/SectionBuilder/SectionWhoIsItFor';
import SectionClientsSpecialists from '../PageBuilder/SectionBuilder/SectionClientsSpecialists';
import SectionCategoryDirectory from '../PageBuilder/SectionBuilder/SectionCategoryDirectory';

const PageBuilder = loadable(() =>
  import(/* webpackChunkName: "PageBuilder" */ '../PageBuilder/PageBuilder')
);

export const LandingPageComponent = props => {
  const { pageAssetsData, inProgress, error, heroSpecialists, onManageDisableScrolling } = props;
  const [showRoleNotice, setShowRoleNotice] = useState(false);
  const handleServiceClickForClient = () => {
    setShowRoleNotice(true);
  };

  const pageBuilderOptions = {
    fieldComponents: {
      internalButtonLink: {
        component: linkProps => (
          <LandingPageAuthAwareLink
            {...linkProps}
            onCustomerServiceClick={handleServiceClickForClient}
          />
        ),
        pickValidProps: exposeLinkProps,
      },
    },
    sectionComponents: {
      customHowItWorks: { component: SectionHowItWorks },
      customWhoIsItFor: { component: SectionWhoIsItFor },
      customClientsSpecialists: { component: SectionClientsSpecialists },
      customCategoryDirectory: { component: SectionCategoryDirectory },
    },
    onCustomerServiceClick: handleServiceClickForClient,
    // D40 hero feed cards: null until fetched (skeleton), [] or < 4 cards → the hero photo
    heroSpecialists,
  };

  return (
    <>
      <LandingPageRoleNoticeModal
        isOpen={showRoleNotice}
        onClose={() => setShowRoleNotice(false)}
        onManageDisableScrolling={onManageDisableScrolling}
      />
      <PageBuilder
        pageAssetsData={pageAssetsData?.[camelize(ASSET_NAME)]?.data}
        inProgress={inProgress}
        error={error}
        fallbackPage={<FallbackPage error={error} />}
        options={pageBuilderOptions}
      />
    </>
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  inProgress: bool,
  error: propTypes.error,
  heroSpecialists: array,
  onManageDisableScrolling: func.isRequired,
};

const mapStateToProps = state => {
  const { pageAssetsData, inProgress, error } = state.hostedAssets || {};
  const { heroSpecialists } = state.LandingPage || {};
  return { pageAssetsData, inProgress, error, heroSpecialists };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(LandingPageComponent);

export default LandingPage;
